import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body5Skeleton } from '../../Skeletons/Skeletons';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Body5 = () => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef(null);

  // Fallback fetch for when used outside HomeDataProvider
  useEffect(() => {
    if (!isOutsideProvider) return;
    const fetchBody5Data = async () => {
      try {
        const [headingsSnap, bentosSnap] = await Promise.all([
          getDocs(collection(db, 'home', 'PopularBentos', 'Headings')),
          getDocs(collection(db, 'home', 'PopularBentos', 'cards')),
        ]);
        const bentos = [];
        bentosSnap.forEach((d) => {
          const data = d.data();
          bentos.push({
            id: d.id,
            title: data.heading || '',
            description: data.subHeading || '',
            chefName: data.chefName || '',
            image: data.img || '',
          });
        });
        bentos.sort((a, b) => a.id.localeCompare(b.id));

        let heading = '';
        let subHeading = '';
        if (!headingsSnap.empty) {
          const hData = headingsSnap.docs[0].data();
          heading = hData.heading || '';
          subHeading = hData.subHeading || '';
        }

        setLocalData({
          heading,
          subHeading,
          bentos,
        });
      } catch (error) {
        console.error('Error fetching Body5 data:', error);
      }
    };
    fetchBody5Data();
  }, [isOutsideProvider]);

  const body5Data = isOutsideProvider ? localData : homeData?.body5;
  const displayBentos = body5Data?.bentos || [];
  const hasCarousel = displayBentos.length > 4;

  const checkScroll = useCallback(() => {
    if (scrollContainerRef.current && hasCarousel) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, [hasCarousel]);

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [checkScroll]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  if (!body5Data || displayBentos.length === 0) {
    return <Body5Skeleton />;
  }

  return (
    <section className="py-8 sm:py-10 lg:py-12 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-orange-50/50">
      {/* Heading */}
      <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-14">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-4">
          {body5Data.heading}
        </h2>
        {body5Data.subHeading && (
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            {body5Data.subHeading}
          </p>
        )}
      </div>

      {/* Bento Cards - Grid or Carousel */}
      <div className="max-w-7xl mx-auto relative group">
        {/* Left Arrow */}
        {hasCarousel && showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white border border-gray-100 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:bg-orange-500 hover:text-white transition-colors duration-200"
            aria-label="Scroll left"
          >
            <FaChevronLeft className="text-base sm:text-lg pr-0.5" />
          </button>
        )}

        {/* Cards Container */}
        <div
          ref={scrollContainerRef}
          className={
            hasCarousel
              ? "flex gap-5 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-4 pt-2"
              : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
          }
          style={hasCarousel ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}
        >
          {displayBentos.map((bento) => (
            <div
              key={bento.id}
              className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col ${
                hasCarousel ? 'min-w-[280px] sm:min-w-[300px] snap-center snap-always' : ''
              }`}
            >
              {/* Image Container */}
              <div className="relative h-48 sm:h-52 bg-gray-50 border-b border-gray-50">
                {bento.image ? (
                  <img
                    src={bento.image}
                    alt={`${bento.title} - Home Cooked Meal`}
                    title={bento.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-orange-50 flex items-center justify-center">
                    <span className="text-orange-200 text-5xl">🍱</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900">
                  {bento.title}
                </h3>

                {/* Chef Name */}
                {bento.chefName && (
                  <p className="text-sm font-semibold text-orange-600 mt-1">
                    {bento.chefName}
                  </p>
                )}

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mt-2 line-clamp-3">
                  {bento.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {hasCarousel && showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 sm:w-12 sm:h-12 bg-white border border-gray-100 rounded-full shadow-md flex items-center justify-center text-gray-600 hover:bg-orange-500 hover:text-white transition-colors duration-200"
            aria-label="Scroll right"
          >
            <FaChevronRight className="text-base sm:text-lg pl-0.5" />
          </button>
        )}
      </div>

      {/* Hide scrollbar CSS */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default Body5;